import { FC, ChangeEvent, useState, useEffect, useId } from 'react';

import { TextAreaProps } from './TextArea.types';
import { StyledDescription, StyledDiv, StyledError } from './TextArea.styles';

export const TextArea: FC<TextAreaProps> = ({
  id,
  name,
  label,
  varient = 'standard',
  minLength,
  maxLength,
  className,
  registerFn,
  errorsProps,
  inputClassName,
  isMandatoryField,
  description,
  registerOptions = {},
  getInputValue,
  inputValue,
  placeHolderText,
  onFocus,
  togglePlaceholder,
  messageError,
}) => {
  const isError = errorsProps?.[id] && 'is-error';
  const messageErrorText = messageError;
  const isOutlined = varient === 'outlined' ? 'outlined' : '';
  const noBorder = varient !== 'outlined' ? 'no-border' : '';

  //value of input field to be returned to parent component
  const [value, setValue] = useState(inputValue);
  const [errorC, setErrorC] = useState(false);

  //state to hide place holder text
  const [isPlaceholderVisible, setPlaceholderVisible] = useState(true);
  const inputId = useId();

  //function to handle onFocus
  const handleFocus = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    if (newValue.length >= 255) {
      setErrorC(true);
    }
    if (!togglePlaceholder) {
      setPlaceholderVisible(false);
    }
    onFocus?.();
  };

  //function to handle onFocus
  const handleBlur = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    if (newValue.length >= 255) {
      setErrorC(true);
    } else {
      setErrorC(false);
    }
  };

  //function to handle input value change
  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    if (newValue.length >= 255) {
      setErrorC(true);
    } else {
      setErrorC(false);
    }
    //return input text value to parent component
    getInputValue?.(newValue);
    if (!newValue) {
      setPlaceholderVisible(true);
    }
  };

  useEffect(() => {
    setValue(inputValue);
    if (!inputValue) {
      setPlaceholderVisible(true);
    }
  }, [inputValue]);

  return (
    <StyledDiv className={className}>
      <fieldset className={[isOutlined, isError, noBorder].join(' ')}>
        <textarea
          key={id}
          name={name}
          id={inputId}
          defaultValue={value}
          value={inputValue}
          minLength={minLength}
          maxLength={maxLength}
          {...(isPlaceholderVisible && placeHolderText ? { placeholder: placeHolderText } : {})}
          aria-label={label}
          className={[inputClassName, isError].join(' ')}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...(registerFn &&
            registerFn(id, {
              ...(isMandatoryField && { required: isMandatoryField }),
              ...(minLength && { minLength: minLength }),
              ...(maxLength && { maxLength: maxLength }),
              ...registerOptions,
            }))}
        />
      </fieldset>
      {description && <StyledDescription>{description}</StyledDescription>}
      {errorC && (
        <StyledError id="error">
          <div className="error-container mt-1">
            <span className="error-icon">
              <i className="icon-alert"></i>
            </span>
            <p className="t-font-xs error-message m-0">{messageErrorText}</p>
          </div>
        </StyledError>
      )}
    </StyledDiv>
  );
};
